import { ReactNode } from 'react'
import { BoxConstraints, RenderBox, RenderBoxProvider, useRenderBox } from '../rendering'

export type FlexProps = {
  direction?: 'row' | 'column'
  gap?: number
  mainAxisAlignment?: 'start' | 'center' | 'end' | 'spaceBetween' | 'spaceAround' | 'spaceEvenly'
  crossAxisAlignment?: 'start' | 'center' | 'end' | 'stretch'
  mainAxisSize?: 'max' | 'min'
}

class FlexRenderBox extends RenderBox {
  performLayout(c: BoxConstraints) {
    const {
      direction = 'column',
      gap = 0,
      mainAxisAlignment = 'start',
      crossAxisAlignment = 'start',
      mainAxisSize = 'max'
    } = this.props as FlexProps
    const isRow = direction === 'row'

    // ------------------------------------------------------------------
    // FIRST PASS → size inflexible children & collect flex information
    // ------------------------------------------------------------------
    const childCount = this.children.length
    const totalGaps = gap * Math.max(childCount - 1, 0)

    let nonFlexMain = 0 // space used by the non-flex children (no gaps)
    let cross = 0
    let flexSum = 0 // sum of flex factors amongst Expanded children

    const loosenForChild = (orig: BoxConstraints): BoxConstraints => {
      if (isRow) {
        // Row → loosen along the horizontal (main) axis so children can be narrower than the parent.
        return { ...orig, minWidth: 0 }
      } else {
        // Column → loosen along the vertical (main) axis so children can be shorter than the parent.
        return { ...orig, minHeight: 0 }
      }
    }

    this.children.forEach((ch) => {
      if (ch instanceof ExpandedRenderBox) {
        flexSum += ch.props.flex ?? 1
        return
      }

      // Size the inflexible child with loosened constraints (matches Flutter)
      ch.performLayout(loosenForChild(c))

      // Accumulate the main-axis space consumed by inflexible children
      nonFlexMain += isRow ? ch.box.width : ch.box.height

      // Track the max cross-axis extent
      cross = Math.max(cross, isRow ? ch.box.height : ch.box.width)
    })

    // Determine how much space is available for flex children.
    const maxConstraintMain = isRow ? c.maxWidth : c.maxHeight
    let free = 0
    if (Number.isFinite(maxConstraintMain)) {
      free = Math.max(maxConstraintMain - nonFlexMain - totalGaps, 0)
    }

    if (flexSum === 0) flexSum = 1 // prevent division-by-zero

    // ------------------------------------------------------------------
    // SECOND PASS → layout flex (Expanded) children
    // ------------------------------------------------------------------
    this.children.forEach((ch) => {
      if (!(ch instanceof ExpandedRenderBox)) return

      const share = (ch.props.flex ?? 1) / flexSum
      const alloc = share * free

      const tight: BoxConstraints = isRow
        ? { minWidth: alloc, maxWidth: alloc, minHeight: 0, maxHeight: c.maxHeight }
        : { minHeight: alloc, maxHeight: alloc, minWidth: 0, maxWidth: c.maxWidth }

      ch.performLayout(tight)

      // Update the max cross-axis extent as flex children get their size
      cross = Math.max(cross, isRow ? ch.box.height : ch.box.width)
    })

    // ------------------------------------------------------------------
    // FINAL SIZE CALCULATION → recompute total main-axis length including gaps
    // ------------------------------------------------------------------
    let main = 0
    this.children.forEach((ch, idx) => {
      main += isRow ? ch.box.width : ch.box.height
      if (idx < childCount - 1) main += gap
    })

    // ---------------------------------------------------------------------
    // Alignment & positioning pass
    // ---------------------------------------------------------------------
    // Decide container size along the main axis.
    let containerMain = main
    if (mainAxisSize === 'max') {
      const maxConstraint = isRow ? c.maxWidth : c.maxHeight
      if (Number.isFinite(maxConstraint)) {
        containerMain = maxConstraint
      }
    }

    const remaining = Math.max(containerMain - main, 0)

    let leading = 0
    let between = gap

    switch (mainAxisAlignment) {
      case 'center':
        leading = remaining / 2
        break
      case 'end':
        leading = remaining
        break
      case 'spaceBetween':
        between = this.children.length > 1 ? remaining / (this.children.length - 1) : 0
        break
      case 'spaceAround':
        between = this.children.length > 0 ? remaining / this.children.length : 0
        leading = between / 2
        break
      case 'spaceEvenly':
        between = this.children.length > 0 ? remaining / (this.children.length + 1) : 0
        leading = between
        break
      // 'start' is the default (nothing to do)
    }

    // Ensure we honour parent min-constraints *before* child positioning when not stretching.
    if (crossAxisAlignment !== 'stretch') {
      if (isRow) {
        cross = Math.max(c.minHeight, cross)
      } else {
        cross = Math.max(c.minWidth, cross)
      }
    }

    let cursor = leading
    this.children.forEach((ch) => {
      if (isRow) {
        // main‑axis position
        ch.box.x = cursor
        cursor += ch.box.width + between

        // cross‑axis alignment
        switch (crossAxisAlignment) {
          case 'center':
            ch.box.y = (cross - ch.box.height) / 2
            break
          case 'end':
            ch.box.y = cross - ch.box.height
            break
          case 'stretch':
            ch.box.y = 0
            ch.box.height = cross
            // If the stretched child is an Expanded wrapper, push the size to its inner box
            if (ch instanceof ExpandedRenderBox && ch.children[0]) {
              ch.children[0].box.height = cross
            }
            break
          default: // 'start' | 'baseline'
            ch.box.y = 0
        }
      } else {
        // main‑axis position
        ch.box.y = cursor
        cursor += ch.box.height + between

        // cross‑axis alignment
        switch (crossAxisAlignment) {
          case 'center':
            ch.box.x = (cross - ch.box.width) / 2
            break
          case 'end':
            ch.box.x = cross - ch.box.width
            break
          case 'stretch':
            ch.box.x = 0
            ch.box.width = cross
            if (ch instanceof ExpandedRenderBox && ch.children[0]) {
              ch.children[0].box.width = cross
            }
            break
          default:
            ch.box.x = 0
        }
      }
    })

    // own size is the space actually used
    this.box.width = isRow ? containerMain : cross
    this.box.height = isRow ? cross : containerMain
  }
}

class ExpandedRenderBox extends RenderBox<{ flex?: number }> {
  performLayout(c: BoxConstraints) {
    const child = this.children[0]

    // No child → occupy the tight slice we were given.
    if (!child) {
      this.box = { x: 0, y: 0, width: c.maxWidth, height: c.maxHeight }
      return
    }

    // Let the child lay itself out with the constraints (tight on main, loose on cross)
    child.performLayout(c)

    // Copy the child's size; do not stretch on the cross‑axis
    this.box = {
      x: 0,
      y: 0,
      width: child.box.width,
      height: child.box.height
    }

    child.box.x = 0
    child.box.y = 0
  }
}

export function Flex({
  children,
  direction = 'column',
  gap = 0,
  mainAxisAlignment = 'start',
  crossAxisAlignment = 'start',
  mainAxisSize = 'max'
}: FlexProps & { children: ReactNode }) {
  const renderBox = useRenderBox(() => {
    return new FlexRenderBox({
      direction,
      gap,
      mainAxisAlignment,
      crossAxisAlignment,
      mainAxisSize
    })
  }, [direction, gap, mainAxisAlignment, crossAxisAlignment, mainAxisSize])
  return <RenderBoxProvider value={renderBox}>{children}</RenderBoxProvider>
}

export function Row({
  children,
  gap = 0,
  mainAxisAlignment = 'start',
  crossAxisAlignment = 'start',
  mainAxisSize = 'max'
}: FlexProps & { children: ReactNode }) {
  return (
    <Flex
      direction="row"
      gap={gap}
      mainAxisAlignment={mainAxisAlignment}
      crossAxisAlignment={crossAxisAlignment}
      mainAxisSize={mainAxisSize}
    >
      {children}
    </Flex>
  )
}

export function Column({
  children,
  gap = 0,
  mainAxisAlignment = 'start',
  crossAxisAlignment = 'start',
  mainAxisSize = 'max'
}: FlexProps & { children: ReactNode }) {
  return (
    <Flex
      direction="column"
      gap={gap}
      mainAxisAlignment={mainAxisAlignment}
      crossAxisAlignment={crossAxisAlignment}
      mainAxisSize={mainAxisSize}
    >
      {children}
    </Flex>
  )
}

export function Expanded({ children, flex = 1 }: { children: ReactNode; flex?: number }) {
  const renderBox = useRenderBox(() => new ExpandedRenderBox({ flex }), [flex])
  return <RenderBoxProvider value={renderBox}>{children}</RenderBoxProvider>
}
